// Permissions de l'espace d'administration, alignées sur les droits de l'API.

// Rôles autorisés à entrer dans l'espace admin
export const ADMIN_ROLES = [
  "superadmin",
  "admin",
  "manager",
  "financier",
  "employee",
];

// Rôles disposant de tous les droits de gestion (création, modification,
// assignation, annulation, paramètres...)
export const MANAGER_ROLES = ["superadmin", "admin", "manager"];

// Pages accessibles en lecture pour les rôles qui ne sont pas gestionnaires
const READ_ONLY_PATHS = {
  financier: [
    "/admin/dashboard",
    "/admin/workers",
    "/admin/gift-cards",
    "/admin/sales-suscribtions",
    "/admin/catalogue",
    "/admin/subscriptions",
    "/admin/products",
    "/admin/inventaires",
    "/admin/rapport",
    "/admin/salon",
  ],
  employee: [
    "/admin/dashboard",
    "/admin/workers",
    "/admin/gift-cards",
    "/admin/catalogue",
    "/admin/subscriptions",
    "/admin/products",
    "/admin/inventaires",
    "/admin/salon",
  ],
};

// Pages réservées à l'admin et au superadmin (journal d'activité)
const ADMIN_ONLY_PATHS = [/^\/admin\/journal\/?$/];

// Pages réservées au superadmin (outils de suppression)
const SUPERADMIN_ONLY_PATHS = [/^\/admin\/outils\/?$/];

// Pages réservées aux gestionnaires même si leur page parente est visible
const MANAGER_ONLY_PATHS = [
  /^\/admin\/subscriptions\/create\/?$/,
  /^\/admin\/subscriptions\/[^/]+\/edit\/?$/,
];

const getRoleName = (user) => user?.role?.name || "";

export const canAccessAdmin = (user) => ADMIN_ROLES.includes(getRoleName(user));

export const isManager = (user) => MANAGER_ROLES.includes(getRoleName(user));

/**
 * Indique si l'utilisateur peut ouvrir une page de l'espace admin.
 * Les liens externes à /admin (ex. "/index") sont toujours autorisés.
 */
export const canAccessPath = (user, path = "") => {
  if (!path || !path.startsWith("/admin")) return true;
  if (SUPERADMIN_ONLY_PATHS.some((regex) => regex.test(path))) {
    return getRoleName(user) === "superadmin";
  }
  if (ADMIN_ONLY_PATHS.some((regex) => regex.test(path))) {
    return ["superadmin", "admin"].includes(getRoleName(user));
  }
  if (isManager(user)) return true;

  const allowed = READ_ONLY_PATHS[getRoleName(user)] || [];

  if (MANAGER_ONLY_PATHS.some((regex) => regex.test(path))) return false;

  return allowed.some(
    (allowedPath) => path === allowedPath || path.startsWith(`${allowedPath}/`),
  );
};

// ---------------------------------------------------------------------------
// Comptes : rôles et hiérarchie (mêmes règles que l'API, user.middleware.ts)

export const ROLE_LABELS = {
  superadmin: "Super admin",
  admin: "Administrateur",
  manager: "Manager",
  financier: "Financier",
  employee: "Employé",
  client: "Client",
};

export const roleLabel = (name) => ROLE_LABELS[name] || name || "";

const ROLE_RANK = { superadmin: 4, admin: 3, manager: 2 };
const rankOf = (roleName) => ROLE_RANK[roleName] ?? 1;

/** Seuls l'admin et le superadmin changent les rôles. */
export const canChangeRoles = (user) =>
  ["superadmin", "admin"].includes(getRoleName(user));

/** Seuls l'admin et le superadmin suppriment des comptes. */
export const canDeleteAccounts = canChangeRoles;

/**
 * Peut-on agir sur ce compte (bloquer, changer le rôle, supprimer) ?
 * Jamais sur son propre compte ; sinon seulement sur un rang inférieur,
 * sauf pour le superadmin.
 */
export const canManageAccount = (actor, target) => {
  if (!actor || !target || actor.id === target.id) return false;
  if (getRoleName(actor) === "superadmin") return true;
  return rankOf(getRoleName(target)) < rankOf(getRoleName(actor));
};

/** Rôles que l'utilisateur peut attribuer à ce compte. */
export const assignableRoles = (actor, target, roles = []) =>
  roles.filter(
    (role) =>
      role.name !== getRoleName(target) &&
      (getRoleName(actor) === "superadmin" ||
        rankOf(role.name) < rankOf(getRoleName(actor))),
  );
